import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, Users, Target, Heart, Shield } from 'lucide-react';
import { motion } from "framer-motion";
import { Button } from "../../../components/ui/button";
import Footer from "../../../components/landing/Footer";

const About: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  return (
    <div className="min-h-screen bg-gray-100 flex flex-col">
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <motion.div
              className="flex items-center gap-2"
              whileHover={{ scale: 1.02 }}
            >
              <Link to="/" className="text-2xl md:text-3xl font-bold">
                <span className="text-primary">Social</span>
                <span className="bg-secondary text-secondary-foreground px-2 py-0.5 rounded-md ml-1">Bunkr</span>
              </Link>
            </motion.div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-8">
              {["Home", "About", "Contact"].map((item) => (
                <Link
                  key={item}
                  to={item === "Home" ? "/" : `/${item.toLowerCase()}`} // Use / for root path
                  className="text-foreground/80 hover:text-primary font-medium transition-colors"
                >
                  {item}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA */}
            <div className="hidden md:flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-foreground hover:text-primary">
                  Login
                </Button>
              </Link>
              <Link to="/signup">
                <Button className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full px-6">
                  Register
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden py-4 border-t border-border"
            >
              <nav className="flex flex-col gap-4">
                {["Home", "About", "Contact"].map((item) => (
                  <Link
                    key={item}
                    to={`/${item.toLowerCase()}`}
                    className="text-foreground/80 hover:text-primary font-medium transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {item}
                  </Link>
                ))}
                <div className="flex flex-col gap-2 pt-4 border-t border-border">
                  <Link to="/login">
                    <Button variant="ghost" className="justify-start">
                      Login
                    </Button>
                  </Link>
                  <Link to="/signup">
                    <Button className="bg-primary text-primary-foreground">
                      Register
                    </Button>
                  </Link>
                </div>
              </nav>
            </motion.div>
          )}
        </div>
      </motion.header>
      <main className="pt-20 md:pt-24 pb-16 flex-grow">
        <div className="container mx-auto px-4">
          {/* Hero Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-4xl mx-auto bg-card rounded-2xl shadow-lg p-8 md:p-12 mt-8"
          >
            <h1 className="text-3xl md:text-4xl font-bold text-primary text-center mb-6">
              About Social Bunkr
            </h1>
            
            <div className="space-y-6 text-foreground/80 leading-relaxed">
              <p>
                Welcome to <span className="font-semibold text-foreground">Social Bunkr</span>, your premier destination for affordable and comfortable stays. Our mission is to provide travelers with budget-friendly accommodation options without compromising on quality or convenience. Whether you're a student, a backpacker, or just looking for a great deal, Social Bunkr is here to help you find your perfect nest away from home.
              </p>
              
              <p>
                We believe that travel should be accessible to everyone. That's why we've partnered with a wide range of properties, from cozy hostels to private rooms, ensuring there's something for every need and budget. Our platform is designed to be user-friendly, making it easy for you to search, compare, and book your next stay with confidence.
              </p>
              
              <p>
                At Social Bunkr, we are committed to transparency, security, and excellent customer service. Join our growing community of happy travelers and discover the joy of affordable exploration.
              </p>
            </div>
          </motion.div>

          {/* Values Grid */}
          <div className="max-w-4xl mx-auto mt-12 grid md:grid-cols-2 gap-6">
            {/* Values from new_fe_changes */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * (1) }}
              className="bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Our Mission</h3>
              <p className="text-foreground/70 text-sm">To provide travelers with budget-friendly accommodation options without compromising on quality or convenience.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * (2) }}
              className="bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Community First</h3>
              <p className="text-foreground/70 text-sm">Building a growing community of happy travelers who discover the joy of affordable exploration together.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * (3) }}
              className="bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Accessibility</h3>
              <p className="text-foreground/70 text-sm">We believe travel should be accessible to everyone, from students to backpackers to anyone looking for a great deal.</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * (4) }}
              className="bg-card rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">Trust & Security</h3>
              <p className="text-foreground/70 text-sm">Committed to transparency, security, and excellent customer service in every interaction.</p>
            </motion.div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default About;